import { useCallback, useState } from "react";
import api from "../services/api";
import { RECON } from "../services/apiConstant";
import { isImageFile } from "./useAIPhotoDescription";
import { compressImagesForAI } from "../utils/imageCompression";
import { presignUpload } from "../services/attachmentService";

export interface ReconStageSuggestion {
  phase: string;
  stage: string;
  description: string;
  estimated_days: number;
  image_indices: number[];
}

export interface ReconSuggestionsResponse {
  suggestions: ReconStageSuggestion[];
}

/** A compressed image and the R2 key it was uploaded to (once). */
export interface UploadedImage {
  file: File;
  objectKey: string;
}

export interface ReconSuggestionsResult {
  suggestions: ReconStageSuggestion[];
  /** Uploaded images in the same order as `image_indices` refer to. Reuse these
   *  keys when attaching on submit — no second upload. */
  uploads: UploadedImage[];
  /** Images whose upload to R2 failed — surfaced so the user can retry them.
   *  The AI analysis ran on the successfully-uploaded subset. */
  failedUploads: File[];
}

export function useAIReconSuggestions() {
  const [isLoading, setIsLoading] = useState(false);

  const suggestReconRequests = useCallback(
    async (files: File[]): Promise<ReconSuggestionsResult> => {
      const imageFiles = files.filter(isImageFile);
      if (imageFiles.length === 0) throw new Error("No image files provided.");

      setIsLoading(true);
      try {
        // Resize in the background (web worker), then upload each image to R2
        // exactly ONCE. The bytes never go through our backend; we send keys.
        const compressed = await compressImagesForAI(imageFiles);

        const results = await Promise.all(compressed.map((file) => presignUpload(file)));
        const uploads: UploadedImage[] = [];
        const failedUploads: File[] = [];
        results.forEach((res, i) => {
          if (res.success && res.object_key) {
            uploads.push({ file: compressed[i], objectKey: res.object_key });
          } else {
            // Don't abort the whole autofill for one bad upload — collect it so the
            // UI can prompt a retry, and analyze the images that did upload.
            failedUploads.push(compressed[i]);
          }
        });

        if (uploads.length === 0) {
          throw new Error("All image uploads failed. Please try again.");
        }

        // Keys line up with image_indices in the response.
        const response = await api.post<ReconSuggestionsResponse>(
          RECON.reconSuggestionsByKeys,
          { image_keys: uploads.map((u) => u.objectKey) },
          { timeout: 120000 } // Gemini analysis can take a while
        );

        return { suggestions: response.data.suggestions, uploads, failedUploads };
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return { suggestReconRequests, isLoading };
}
